import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  shorthands,
  TabList,
  Tab,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Label,
  Dropdown,
  Option,
  Input,
  Textarea,
  ToggleButton,
} from '@fluentui/react-components';
import { Calendar24Regular, Add20Regular } from '@fluentui/react-icons';
import MaintenanceActiveReminders from './MaintenanceActiveReminders';
import MaintenanceHistory from './MaintenanceHistory';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#edf1f5',
  },
  tabContainer: {
    ...shorthands.padding('0', '16px'),
  },
  tabContent: {
    flex: 1,
    overflow: 'auto',
  },
  notesTextarea: {
    width: '100%',
    marginTop: '8px',
  },
});

// Create a context for the active tab
export const MaintenanceTabContext = React.createContext<string>('active');

export default function Maintenance() {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState('active');
  const [oneTimeServiceDialogOpen, setOneTimeServiceDialogOpen] = useState(false);
  const [addReminderDialogOpen, setAddReminderDialogOpen] = useState(false);

  // One-time service form state
  const [serviceType, setServiceType] = useState<'Tracker' | 'Other Item'>('Tracker');
  const [group, setGroup] = useState('All');
  const [tracker, setTracker] = useState('');
  const [serviceTypeSelection, setServiceTypeSelection] = useState('');
  const [completionDate, setCompletionDate] = useState('Wed, 10/29/2025');
  const [cost, setCost] = useState('');
  const [odometer, setOdometer] = useState('');
  const [notes, setNotes] = useState('');

  // Add reminder form state
  const [reminderServiceType, setReminderServiceType] = useState<'Tracker' | 'Other Item'>('Tracker');
  const [reminderGroup, setReminderGroup] = useState('All');
  const [reminderTracker, setReminderTracker] = useState('');
  const [reminderServiceTypeSelection, setReminderServiceTypeSelection] = useState('');
  const [reminderType, setReminderType] = useState('');
  const [reminderEmailNotifications, setReminderEmailNotifications] = useState('');
  const [reminderTextNotifications, setReminderTextNotifications] = useState('');

  // Dispatch a custom event when the tab changes so the Header can update
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('maintenanceTabChange', { detail: selectedTab }));
  }, [selectedTab]);

  // Listen for export requests
  useEffect(() => {
    const handleExport = () => {
      window.dispatchEvent(new CustomEvent('maintenanceExport', { detail: selectedTab }));
    };

    window.addEventListener('triggerMaintenanceExport', handleExport);
    return () => {
      window.removeEventListener('triggerMaintenanceExport', handleExport);
    };
  }, [selectedTab]);

  // Listen for print requests
  useEffect(() => {
    const handlePrint = () => {
      window.dispatchEvent(new CustomEvent('maintenancePrint', { detail: selectedTab }));
    };

    window.addEventListener('triggerMaintenancePrint', handlePrint);
    return () => {
      window.removeEventListener('triggerMaintenancePrint', handlePrint);
    };
  }, [selectedTab]);

  // Listen for one-time service event creation
  useEffect(() => {
    const handleOneTimeService = () => {
      setOneTimeServiceDialogOpen(true);
    };

    window.addEventListener('triggerOneTimeService', handleOneTimeService);
    return () => {
      window.removeEventListener('triggerOneTimeService', handleOneTimeService);
    };
  }, []);

  // Listen for add new reminder
  useEffect(() => {
    const handleAddReminder = () => {
      setAddReminderDialogOpen(true);
    };

    window.addEventListener('triggerAddReminder', handleAddReminder);
    return () => {
      window.removeEventListener('triggerAddReminder', handleAddReminder);
    };
  }, []);

  return (
    <MaintenanceTabContext.Provider value={selectedTab}>
      <div className={styles.container}>
        <div className={styles.tabContainer}>
          <TabList
            selectedValue={selectedTab}
            onTabSelect={(_, data) => setSelectedTab(data.value as string)}
          >
            <Tab value="active">Active Reminders</Tab>
            <Tab value="history">Maintenance History</Tab>
          </TabList>
        </div>
        <div className={styles.tabContent}>
          {selectedTab === 'active' ? <MaintenanceActiveReminders /> : <MaintenanceHistory />}
        </div>
      </div>

      {/* One-Time Service Dialog */}
      <Dialog open={oneTimeServiceDialogOpen} onOpenChange={(_, data) => setOneTimeServiceDialogOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>One-Time Service</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <Label>Type</Label>
                  <div style={{ display: 'flex', gap: '0px', marginTop: '8px' }}>
                    <ToggleButton
                      appearance="outline"
                      checked={serviceType === 'Tracker'}
                      onClick={() => setServiceType('Tracker')}
                      style={{
                        flex: 1,
                        borderRadius: '4px 0 0 4px',
                        backgroundColor: serviceType === 'Tracker' ? '#000000' : 'transparent',
                        color: serviceType === 'Tracker' ? '#ffffff' : 'inherit',
                      }}
                    >
                      Tracker
                    </ToggleButton>
                    <ToggleButton
                      appearance="outline"
                      checked={serviceType === 'Other Item'}
                      onClick={() => setServiceType('Other Item')}
                      style={{
                        flex: 1,
                        borderRadius: '0 4px 4px 0',
                        borderLeft: 'none',
                      }}
                    >
                      Other Item
                    </ToggleButton>
                  </div>
                </div>

                <div>
                  <Label htmlFor="group">Group</Label>
                  <Dropdown
                    id="group"
                    value={group}
                    onOptionSelect={(_, data) => setGroup(data.optionText || 'All')}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    <Option>All</Option>
                    <Option>Fleet A</Option>
                    <Option>Fleet B</Option>
                  </Dropdown>
                </div>

                <div>
                  <Label htmlFor="tracker">Tracker</Label>
                  <Dropdown
                    id="tracker"
                    placeholder="Select a Tracker"
                    value={tracker}
                    onOptionSelect={(_, data) => setTracker(data.optionText || '')}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    <Option>X2A-YKI BLUE MACK</Option>
                    <Option>Philly Mack Rolloff</Option>
                    <Option>Gringo FL Mack</Option>
                  </Dropdown>
                </div>

                <div>
                  <Label htmlFor="serviceType">Service Type</Label>
                  <Dropdown
                    id="serviceType"
                    placeholder="Select Service Type"
                    value={serviceTypeSelection}
                    onOptionSelect={(_, data) => setServiceTypeSelection(data.optionText || '')}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    <Option>Oil Change</Option>
                    <Option>Air Filter</Option>
                  </Dropdown>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label htmlFor="completionDate">Service Completion Date</Label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                      <Input
                        id="completionDate"
                        value={completionDate}
                        onChange={(e, data) => setCompletionDate(data.value)}
                        style={{ width: '100%' }}
                      />
                      <Button appearance="subtle" icon={<Calendar24Regular />} />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="cost">Cost</Label>
                    <Input
                      id="cost"
                      value={cost}
                      onChange={(e, data) => setCost(data.value)}
                      style={{ width: '100%', marginTop: '8px' }}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="odometer">Odometer (km)</Label>
                  <Input
                    id="odometer"
                    value={odometer}
                    onChange={(e, data) => setOdometer(data.value)}
                    style={{ width: '100%', marginTop: '8px' }}
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    size="large"
                    rows={3}
                    value={notes}
                    onChange={(_, data) => setNotes(data.value)}
                    textarea={{ className: styles.notesTextarea }}
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setOneTimeServiceDialogOpen(false)}>
                CANCEL
              </Button>
              <Button appearance="primary" onClick={() => {
                console.log('Creating one-time service:', {
                  serviceType,
                  group,
                  tracker,
                  serviceTypeSelection,
                  completionDate,
                  cost,
                  odometer,
                  notes,
                });
                setOneTimeServiceDialogOpen(false);
              }}>
                SAVE
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Add New Service Reminder Dialog */}
      <Dialog open={addReminderDialogOpen} onOpenChange={(_, data) => setAddReminderDialogOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Add New Service Reminder</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <Label>Type</Label>
                  <div style={{ display: 'flex', gap: '0px', marginTop: '8px' }}>
                    <ToggleButton
                      appearance="outline"
                      checked={reminderServiceType === 'Tracker'}
                      onClick={() => setReminderServiceType('Tracker')}
                      style={{
                        flex: 1,
                        borderRadius: '4px 0 0 4px',
                        backgroundColor: reminderServiceType === 'Tracker' ? '#000000' : 'transparent',
                        color: reminderServiceType === 'Tracker' ? '#ffffff' : 'inherit',
                      }}
                    >
                      Tracker
                    </ToggleButton>
                    <ToggleButton
                      appearance="outline"
                      checked={reminderServiceType === 'Other Item'}
                      onClick={() => setReminderServiceType('Other Item')}
                      style={{
                        flex: 1,
                        borderRadius: '0 4px 4px 0',
                        borderLeft: 'none',
                      }}
                    >
                      Other Item
                    </ToggleButton>
                  </div>
                </div>

                <div>
                  <Label htmlFor="reminderGroup">Group</Label>
                  <Dropdown
                    id="reminderGroup"
                    value={reminderGroup}
                    onOptionSelect={(_, data) => setReminderGroup(data.optionText || 'All')}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    <Option>All</Option>
                    <Option>Fleet A</Option>
                    <Option>Fleet B</Option>
                  </Dropdown>
                </div>

                <div>
                  <Label htmlFor="reminderTracker">Tracker</Label>
                  <Dropdown
                    id="reminderTracker"
                    placeholder="Select a Tracker"
                    value={reminderTracker}
                    onOptionSelect={(_, data) => setReminderTracker(data.optionText || '')}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    <Option>X2A-YKI BLUE MACK</Option>
                    <Option>Philly Mack Rolloff</Option>
                    <Option>Gringo FL Mack</Option>
                  </Dropdown>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label htmlFor="reminderServiceType">Service Type</Label>
                    <Dropdown
                      id="reminderServiceType"
                      placeholder="Select a Service Type"
                      value={reminderServiceTypeSelection}
                      onOptionSelect={(_, data) => setReminderServiceTypeSelection(data.optionText || '')}
                      style={{ width: '100%', marginTop: '8px' }}
                    >
                      <Option>Oil Change</Option>
                      <Option>Air Filter</Option>
                    </Dropdown>
                  </div>

                  <div>
                    <Label htmlFor="reminderType">Type of Reminder</Label>
                    <Dropdown
                      id="reminderType"
                      placeholder="Select a Reminder Type"
                      value={reminderType}
                      onOptionSelect={(_, data) => setReminderType(data.optionText || '')}
                      style={{ width: '100%', marginTop: '8px' }}
                    >
                      <Option>Kilometer Based</Option>
                      <Option>Time Based</Option>
                      <Option>Engine Hours Based</Option>
                    </Dropdown>
                  </div>
                </div>

                <div style={{ backgroundColor: '#3d3d3d', color: '#ffffff', padding: '8px 12px', marginTop: '8px' }}>
                  <Label style={{ color: '#ffffff', fontWeight: '600' }}>Reminder Alerts</Label>
                </div>

                <div>
                  <Label htmlFor="reminderEmailNotifications">Email Notifications</Label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                    <Input
                      id="reminderEmailNotifications"
                      value={reminderEmailNotifications}
                      onChange={(e, data) => setReminderEmailNotifications(data.value)}
                      style={{ width: '100%' }}
                    />
                    <Button appearance="subtle" icon={<Add20Regular />} />
                  </div>
                </div>

                <div>
                  <Label htmlFor="reminderTextNotifications">
                    Text Message Notifications <span style={{ fontSize: '12px', fontStyle: 'italic' }}>(standard text rates apply)</span>
                  </Label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                    <Input
                      id="reminderTextNotifications"
                      value={reminderTextNotifications}
                      onChange={(e, data) => setReminderTextNotifications(data.value)}
                      style={{ width: '100%' }}
                    />
                    <Button appearance="subtle" icon={<Add20Regular />} />
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setAddReminderDialogOpen(false)}>
                CANCEL
              </Button>
              <Button appearance="primary" onClick={() => {
                console.log('Adding new reminder:', {
                  serviceType: reminderServiceType,
                  group: reminderGroup,
                  tracker: reminderTracker,
                  serviceTypeSelection: reminderServiceTypeSelection,
                  reminderType,
                  emailNotifications: reminderEmailNotifications,
                  textNotifications: reminderTextNotifications,
                });
                setAddReminderDialogOpen(false);
              }}>
                SAVE
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </MaintenanceTabContext.Provider>
  );
}
